import { useSelector } from "react-redux";
import CustomAccordion from "../controls/CustomAccordion";
import { RootState } from "../../store/store";
import { Divider, TextField } from "@mui/material";
import { CustomCheckbox } from "../controls/StyledCheckbox";
import { useCallback, useEffect, useMemo, useState } from "react";
import classes from "./SidebarFilter.module.scss";
import { filteredArray } from "../../utils/helper";
import { VariableSizeList as List } from "react-window"; // Virtualized list

type FilterProps = {
  title: string;
  filterKey: keyof RootState["filter"]["selectedFilter"]; // Key for Redux state
  localFilter?: any;
  options: string[]; // List of options
  optionLabelMap?: Record<string, string>;
  isSearchable?: boolean;
  isVirtualized?: boolean;
  showSelectAll?: boolean;
  setLocalFilter: React.Dispatch<any>;
};

const GenericFilter: React.FC<FilterProps> = ({
  title,
  filterKey,
  localFilter,
  options,
  optionLabelMap = {},
  isSearchable = false,
  isVirtualized = false,
  showSelectAll = false,
  setLocalFilter = null,
}) => {
  const { selectedFilter } = useSelector((state: RootState) => state.filter);
  const [search, setSearch] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    new Set(selectedFilter[filterKey] as string[])
  );

  // Sync local state with Redux (for external updates like Global Filters)
  useEffect(() => {
    const reduxItems = new Set(selectedFilter[filterKey] as string[]);

    setSelectedItems((prev) => {
      return JSON.stringify(Array.from(reduxItems)) !==
        JSON.stringify(Array.from(prev))
        ? reduxItems
        : prev;
    });
  }, [selectedFilter, filterKey]);

  useEffect(() => {
    const items = new Set(localFilter[filterKey] as string[]);

    setSelectedItems((prev) => {
      return JSON.stringify(Array.from(items)) !==
        JSON.stringify(Array.from(prev))
        ? items
        : prev;
    });
  }, [localFilter, filterKey]);

  // Memoize filtered options
  const filteredOptions = useMemo(
    () => filteredArray(options, search),
    [options, search]
  );

  // Check if "All" is selected
  const isAllSelected = useMemo(
    () => selectedItems.size === options?.length,
    [selectedItems, options]
  );

  // Handle checkbox selection
  const handleItemChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const currentItem = e.target.value;
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        if (checked) newSet.add(currentItem);
        else newSet.delete(currentItem);
        return newSet;
      });
    },
    []
  );

  // Handle "Select All"
  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setSelectedItems(checked ? new Set(options) : new Set());
    },
    [options]
  );

  useEffect(() => {
    if (setLocalFilter) {
      setLocalFilter((prev: any) => {
        const newFilter = { ...prev, [filterKey]: Array.from(selectedItems) };
        return JSON.stringify(prev) !== JSON.stringify(newFilter)
          ? newFilter
          : prev;
      });
    }
  }, [selectedItems, filterKey, setLocalFilter]);

  const getRowHeight = (index: number) => {
    const textLength = filteredOptions[index].length;
    return textLength > 50 ? 60 : textLength > 30 ? 50 : 40;
  };

  return (
    <>
      <CustomAccordion title={title} badge badgeCount={selectedItems.size}>
        <div className={classes["riskTypeFilterOption"]}>
          {/* Optional Search Input */}
          {isSearchable && (
            <TextField
              label={`Search ${title}`}
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="riskTypeSearch"
              autoComplete="off"
            />
          )}

          {/* "Select All" Checkbox */}
          {showSelectAll && (
            <div className={classes["riskTypeFilter"]}>
              <CustomCheckbox
                checked={isAllSelected}
                onChange={handleSelectAll}
                size="small"
                value="all"
                id={`select-all-${title}`}
              />
              <label htmlFor={`select-all-${title}`}>All</label>
            </div>
          )}

          {/* Virtualized List (for large datasets) */}
          {filteredOptions?.length > 0 && isVirtualized ? (
            <List
              height={200}
              itemCount={filteredOptions.length}
              itemSize={getRowHeight}
              width={"100%"}
            >
              {({ index, style }) => (
                <div
                  style={style}
                  key={filteredOptions[index]}
                  className={classes["riskTypeFilter"]}
                >
                  <CustomCheckbox
                    checked={selectedItems.has(filteredOptions[index])}
                    onChange={handleItemChange}
                    size="small"
                    value={filteredOptions[index]}
                    id={`${filterKey.toString()}-${filteredOptions[index]}`}
                  />
                  <label
                    htmlFor={`${filterKey.toString()}-${
                      filteredOptions[index]
                    }`}
                    //Classname is only added for evaluation filter to add color dots infront of the text
                    className={
                      title === "Evaluation"
                        ? classes[
                            `${filteredOptions[index]
                              .toLowerCase()
                              .split(" ")
                              .join("-")}-dot`
                          ]
                        : ""
                    }
                  >
                    {optionLabelMap[filteredOptions[index]] ||
                      filteredOptions[index]}
                  </label>
                </div>
              )}
            </List>
          ) : (
            filteredOptions?.map((option) => (
              <div key={option} className={classes["riskTypeFilter"]}>
                <CustomCheckbox
                  checked={selectedItems.has(option)}
                  onChange={handleItemChange}
                  size="small"
                  value={option}
                  id={`${filterKey.toString()}-${option}`}
                />
                <label
                  htmlFor={`${filterKey.toString()}-${option}`}
                  className={
                    title === "Evaluation"
                      ? classes[
                          `${option.toLowerCase().split(" ").join("-")}-dot`
                        ]
                      : ""
                  }
                >
                  {optionLabelMap[option] || option}
                </label>
              </div>
            ))
          )}

          {filteredOptions?.length === 0 && (
            <div className={classes["no-results"]}>No results found</div>
          )}
        </div>
      </CustomAccordion>
      <Divider />
    </>
  );
};

export default GenericFilter;
